import { useState, useEffect, useMemo } from "react";
import { Stack, Button, Title, InfoCard, useActions, useToast } from "@introist/react-foundation/v2";
import { api } from "services/rpc/RpcProvider";

export const BulkCancelButton = ({
  selectedSteps,
  onCompleted
}: {
  selectedSteps: string[];
  onCompleted: () => void;
}) => {
  const [stage, setStage] = useState<"notstarted" | "inprogress" | "completed">("notstarted");
  const [handledSteps, setHandledSteps] = useState<string[]>([]);
  const [errorSteps, setErrorSteps] = useState<string[]>([]);

  const toast = useToast();
  const { onConfirmAction } = useActions();
  const cancelStepOp = api.automations.steps.cancel.useMutation();
  const apiUtils = api.useContext();

  useEffect(() => {
    if (stage === "completed" && selectedSteps.length === 0) return;
    if (stage !== "notstarted" && selectedSteps.length > 0) {
      setStage("notstarted");
    }
  }, [selectedSteps, stage]);

  const cancelAllSteps = async () => {
    setHandledSteps([]);
    setErrorSteps([]);
    setStage("inprogress");

    for (const stepId of selectedSteps) {
      await cancelStepOp
        .mutateAsync({ id: stepId })
        .then(() => {
          setHandledSteps(prev => [...prev, stepId]);
        })
        .catch(() => {
          setErrorSteps(prev => [...prev, stepId]);
        });
    }

    // Refresh the step lists after all cancellations
    await apiUtils.journeys.steps.list.invalidate();
    await apiUtils.automations.find.invalidate();
    await apiUtils.automations.steps.find.invalidate();

    setStage("completed");

    if (errorSteps.length === 0) {
      toast.success(`Successfully canceled ${selectedSteps.length} step${selectedSteps.length > 1 ? "s" : ""}`);
    } else {
      toast.error(
        `Canceled ${handledSteps.length} steps with ${errorSteps.length} error${errorSteps.length > 1 ? "s" : ""}`
      );
    }

    onCompleted();
  };

  const handleCancelClick = onConfirmAction(
    cancelAllSteps,
    {
      title: "Cancel steps",
      description: (
        <InfoCard icon="crossCircle" title="Cancel steps" colorVariant="danger">
          Are you sure you want to cancel {selectedSteps.length} step{selectedSteps.length > 1 ? "s" : ""}?
          This cannot be undone.
        </InfoCard>
      )
    },
    "Failed to cancel steps"
  );

  const title = useMemo(() => {
    if (stage === "notstarted") {
      return selectedSteps.length === 1
        ? "1 step selected"
        : `${selectedSteps.length} steps selected`;
    }
    if (stage === "inprogress") {
      return `${handledSteps.length}/${selectedSteps.length} canceled`;
    }
    if (stage === "completed" && errorSteps.length === 0) {
      return "Completed!";
    }
    if (stage === "completed" && errorSteps.length > 0) {
      return `Completed with ${errorSteps.length} error${errorSteps.length > 1 ? "s" : ""}`;
    }
  }, [selectedSteps, handledSteps, stage, errorSteps.length]);

  if (selectedSteps.length === 0) return null;

  return (
    <Stack
      style={{
        padding: "12px 16px",
        backgroundColor: "var(--palette-surface-dimmed)",
        borderTop: "1px solid var(--palette-border-default)"
      }}
    >
      <Title>{title}</Title>
      <Button
        onClick={handleCancelClick}
        size="small"
        disabled={selectedSteps.length === 0 || stage === "inprogress"}
      >
        Cancel {selectedSteps.length} step{selectedSteps.length > 1 ? "s" : ""}
      </Button>
    </Stack>
  );
};
